import React from "react";
import { cn } from "../../utils/cn";

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  key?: string;
  label?: string;
  className?: string;
  /** Applied to the actual <input> element (e.g. "text-center"), unlike
   *  className which only reaches the outer bordered box */
  inputClassName?: string;
  value?: any;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
  name?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  optional?: boolean;
  error?: boolean;
  helperText?: string;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  /** Rounded, borderless search-bar look (e.g. list page filter bars) */
  pill?: boolean;
}

const Input: React.FC<InputProps> = ({
  key = "",
  label,
  error,
  helperText,
  startAdornment,
  endAdornment,
  className,
  inputClassName,
  required = false,
  optional = false,
  pill = false,
  ...props
}) => {
  return (
    <div key={key} className="flex flex-col items-start space-y-1">
      {label && (
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {label} {optional && <span>(optional)</span>}{" "}
          {required && <span className="text-red-600">*</span>}
        </label>
      )}

      <div
        className={cn(
          "w-full flex items-center text-sm h-9",
          pill
            ? "rounded-full bg-gray-50 border border-gray-200 focus-within:bg-white focus-within:border-indigo-500 dark:bg-gray-800 dark:border-gray-700 dark:focus-within:bg-gray-800 dark:focus-within:border-primary-500 px-4"
            : "border rounded-md bg-white focus-within:border-indigo-500 dark:bg-gray-800 dark:focus-within:border-primary-500 px-2",
          !pill &&
            (error
              ? "border-red-500"
              : "border-gray-300 dark:border-gray-700"),
          props.disabled && "bg-gray-100 cursor-not-allowed dark:bg-gray-700",
          className
        )}
      >
        {/* Start Adornment */}
        {startAdornment && (
          <div className="mr-1 text-gray-500 dark:text-gray-400 flex items-center shrink-0">
            {typeof startAdornment === "number" ||
            typeof startAdornment === "string" ? (
              <span>{startAdornment}</span>
            ) : (
              startAdornment
            )}
          </div>
        )}

        {/* Input Field */}
        <input
          {...props}
          className={cn(
            "flex-1 bg-transparent text-gray-900 placeholder-gray-400 outline-none px-1 dark:text-gray-100 dark:placeholder-gray-500",
            "min-w-0 h-full", // ensures no overflow
            props.disabled && "cursor-not-allowed text-gray-500",
            inputClassName
          )}
        />

        {/* End Adornment */}
        {endAdornment && (
          <div className="ml-1 text-gray-500 dark:text-gray-400 flex items-center shrink-0">
            {typeof endAdornment === "number" ||
            typeof endAdornment === "string" ? (
              <span>{endAdornment}</span>
            ) : (
              endAdornment
            )}
          </div>
        )}
      </div>

      {helperText && (
        <span
          className={`text-xs ${
            error ? "text-red-500" : "text-gray-500 dark:text-gray-400"
          }`}
        >
          {helperText}
        </span>
      )}
    </div>
  );
};

export default Input;
